import { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import AdminUsers from './AdminUsers';
import AdminSessions from './AdminSessions';
import AdminAnalytics from './AdminAnalytics';
import AdminCalendar from './AdminCalendar';
import AdminSystem from './AdminSystem';
import AdminSupport from './AdminSupport';
import '../../components/layout/Layout.css';

function AdminHome({ supportBadge }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/admin/stats')
      .then(res => setStats(res.data.stats))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">DASHBOARD</h1>
        <p className="page-subtitle">Welcome back. Here's what's happening.</p>
      </div>

      {supportBadge > 0 && (
        <div onClick={() => navigate('/admin/support')} style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
          background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.25)',
          borderRadius: 'var(--radius-md)', marginBottom: '24px', cursor: 'pointer',
          transition: 'background 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(96,165,250,0.14)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(96,165,250,0.08)'}>
          <span style={{ fontSize: '1.1rem' }}>🎫</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#60a5fa' }}>
              {supportBadge} new support {supportBadge === 1 ? 'reply' : 'replies'} waiting
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '8px' }}>
              — click to view
            </span>
          </div>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>→</span>
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading stats...</p>
      ) : stats && (
        <div className="stats-grid">
          <div className="stat-card"><div className="stat-card-label">Instructors</div><div className="stat-card-value">{stats.total_instructors}</div></div>
          <div className="stat-card"><div className="stat-card-label">Parents</div><div className="stat-card-value">{stats.total_parents}</div></div>
          <div className="stat-card"><div className="stat-card-label">Today's Sessions</div><div className="stat-card-value">{stats.sessions_today}</div></div>
          <div className="stat-card"><div className="stat-card-label">Upcoming</div><div className="stat-card-value">{stats.upcoming_sessions}</div></div>
          <div className="stat-card"><div className="stat-card-label">Confirmed</div><div className="stat-card-value">{stats.total_confirmed_sessions}</div></div>
          <div className="stat-card"><div className="stat-card-label">Pending</div><div className="stat-card-value">{stats.total_pending_sessions}</div></div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [supportBadge, setSupportBadge] = useState(0);

  const fetchSupportBadge = useCallback(() => {
    axios.get('/api/support/notifications').then(r => setSupportBadge(r.data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get('/api/maintenance/status').then(r => setMaintenanceMode(r.data.maintenance)).catch(() => {});
    fetchSupportBadge();
  }, [fetchSupportBadge]);

  const navItems = useMemo(() => [
    { path: '/admin', label: 'Dashboard', icon: '▦', exact: true },
    { path: '/admin/users', label: 'Users', icon: '👥' },
    { path: '/admin/sessions', label: 'Sessions', icon: '📋' },
    { path: '/admin/analytics', label: 'Analytics', icon: '📊' },
    { path: '/admin/calendar', label: 'Calendar', icon: '📆' },
    { path: '/admin/support', label: 'Support', icon: '🎫', badge: supportBadge },
    { path: '/admin/system', label: 'System', icon: '⚙' },
  ], [supportBadge]);

  const toggleMaintenance = async () => {
    setMaintenanceLoading(true);
    try {
      const r = await axios.post('/api/maintenance/toggle');
      setMaintenanceMode(r.data.maintenance);
    } catch (err) { console.error(err); }
    finally { setMaintenanceLoading(false); }
  };

  return (
    <Layout navItems={navItems}>
      {/* Maintenance Mode Banner */}
      <div style={{ padding:'8px 24px', background: maintenanceMode ? 'rgba(248,113,113,0.1)' : 'transparent', borderBottom: maintenanceMode ? '1px solid rgba(248,113,113,0.3)' : 'none', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'8px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          {maintenanceMode && <span style={{ color:'#f87171', fontSize:'0.85rem', fontWeight:'600' }}>🔧 MAINTENANCE MODE IS ACTIVE — Users see maintenance page</span>}
        </div>
        <button
          onClick={toggleMaintenance}
          disabled={maintenanceLoading}
          style={{
            padding:'7px 16px', borderRadius:'var(--radius-md)',
            border: maintenanceMode ? '1px solid #f87171' : '1px solid var(--border)',
            background: maintenanceMode ? 'rgba(248,113,113,0.15)' : 'var(--bg-elevated)',
            color: maintenanceMode ? '#f87171' : 'var(--text-secondary)',
            fontSize:'0.8rem', fontWeight:'600', cursor:'pointer',
            display:'flex', alignItems:'center', gap:'6px', whiteSpace:'nowrap'
          }}>
          {maintenanceLoading ? '...' : maintenanceMode ? '🔧 Disable Maintenance Mode' : '🔧 Enable Maintenance Mode'}
        </button>
      </div>
      <Routes>
        <Route index element={<AdminHome supportBadge={supportBadge} />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="sessions" element={<AdminSessions />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="calendar" element={<AdminCalendar />} />
        <Route path="support" element={<AdminSupport onNotifRead={fetchSupportBadge} />} />
        <Route path="system" element={<AdminSystem />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Layout>
  );
}
